import React, { useCallback, useEffect, useState } from "react";
import { ArrowUpRight, FileText, Printer, Trash2, UserCog } from "lucide-react";
import { practiceApi, openPracticeFile, formatDate } from "../../lib/practiceApi";
import "./coordination.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SITE_FIELDS = [["site_name", "Cantiere"], ["site_address", "Ubicazione"], ["municipality", "Comune"],
                     ["works_description", "Natura dell'opera"], ["permit_reference", "Titolo edilizio"],
                     ["works_start", "Inizio lavori", "date"], ["works_end", "Fine lavori", "date"]];
const CLIENT_FIELDS = [["client_name", "Committente"], ["client_fiscal_code", "C.F. / P. IVA"], ["client_address", "Sede / residenza"],
                       ["client_legal_rep", "Rappresentante legale"], ["client_legal_rep_role", "Qualifica del rappresentante"]];
const TECH_FIELDS = [["technician_name", "Tecnico"], ["technician_birth", "Nato a / il"], ["technician_fiscal_code", "Codice fiscale"],
                     ["technician_residence", "Residenza"], ["technician_office", "Studio"], ["technician_order_register", "Iscrizione albo"],
                     ["technician_policy", "Polizza professionale"]];
const MANAGER_FIELDS = [["works_manager_name", "Responsabile dei Lavori"], ["works_manager_fiscal_code", "Codice fiscale"],
                        ["works_manager_residence", "Residenza"], ["works_manager_office", "Studio / sede"]];
const PSC_FIELDS = [["psc_title", "Titolo del PSC"], ["psc_revision", "Revisione"], ["psc_date", "Data del piano", "date"],
                    ["psc_delivery_date", "Data di consegna", "date"], ["psc_delivery_mode", "Modalità di consegna"], ["psc_protocol", "Protocollo"]];
const GROUPS = [["Cantiere", SITE_FIELDS], ["Committente", CLIENT_FIELDS], ["Tecnico dello studio", TECH_FIELDS],
                ["Responsabile dei Lavori", MANAGER_FIELDS], ["Piano di Sicurezza e Coordinamento", PSC_FIELDS]];

export const ClientSection = ({ siteId }) => {
  const [state, setState] = useState(null);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const data = await practiceApi(`/coordination/${siteId}/client-docs`);
      setState(data);
      setForm(current => (Object.keys(current).length ? current : data.prefill));
    } catch (err) { setError(err.message); }
  }, [siteId]);
  useEffect(() => { load(); }, [load]);

  async function generate(kind, fmt) {
    setBusy(`${kind}-${fmt}`); setError("");
    try {
      const response = await fetch(`${API}/coordination/${siteId}/client-docs/${kind}?fmt=${fmt}`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ data: form }),
      });
      if (!response.ok) throw new Error((await response.json().catch(() => ({}))).detail || "Generazione non riuscita");
      const url = URL.createObjectURL(await response.blob());
      const link = document.createElement("a");
      link.href = url;
      if (fmt === "pdf") { link.target = "_blank"; link.rel = "noopener noreferrer"; } else link.download = `${kind}.docx`;
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      await load();
    } catch (err) { setError(err.message); } finally { setBusy(""); }
  }
  async function removeDoc(file) {
    if (!window.confirm("Rimuovere il documento archiviato?")) return;
    try { await practiceApi(`/files/${file.id}`, { method: "DELETE" }); await load(); }
    catch (err) { setError(err.message); }
  }

  if (!state) return null;

  return <section className="client-docs" data-testid="coordination-client-section">
    <div className="practice-section-heading">
      <h2><UserCog size={16} />Committente</h2>
      <span>Documenti di nomina e verifica dei requisiti · dati recuperati da cantiere, anagrafica e impostazioni dello studio</span>
    </div>
    {error && <div className="error" role="alert" data-testid="client-docs-error">{error}</div>}

    <details className="client-docs-data" data-testid="client-docs-data">
      <summary>Dati utilizzati nei documenti · modificabili prima della generazione</summary>
      {GROUPS.map(([label, fields]) => <fieldset className="practice-fieldset" key={label}><legend>{label}</legend>
        <div className="practice-form-grid">
          {fields.map(([key, title, kind]) => <label key={key}>{title}
            <input type={kind === "date" ? "date" : "text"} data-testid={`client-doc-field-${key}`} value={form[key] || ""}
              onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} />
          </label>)}
        </div>
      </fieldset>)}
      <fieldset className="practice-fieldset"><legend>Note e luogo</legend><div className="practice-form-grid">
        <label>Luogo<input data-testid="client-doc-field-place" value={form.place || ""} onChange={e => setForm(f => ({ ...f, place: e.target.value }))} /></label>
        <label>Data del documento<input type="date" data-testid="client-doc-field-date" value={form.date || ""} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} /></label>
        <label className="field-wide">Note<textarea rows={2} data-testid="client-doc-field-notes" value={form.notes || ""} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} /></label>
      </div></fieldset>
    </details>

    <div className="client-docs-grid">
      {state.kinds.map(item => <article key={item.kind} data-testid={`client-doc-card-${item.kind}`}>
        <strong>{item.title}</strong>
        <small>{item.reference}</small>
        <div className="client-docs-actions">
          <button className="primary" data-testid={`client-doc-pdf-${item.kind}`} disabled={!!busy} onClick={() => generate(item.kind, "pdf")}>
            <Printer size={14} />{busy === `${item.kind}-pdf` ? "Genero…" : "PDF"}
          </button>
          <button className="ghost" data-testid={`client-doc-docx-${item.kind}`} disabled={!!busy} onClick={() => generate(item.kind, "docx")}>
            <FileText size={14} />{busy === `${item.kind}-docx` ? "Genero…" : "Word"}
          </button>
        </div>
      </article>)}
    </div>

    <div className="client-docs-archive" data-testid="client-docs-archive">
      <strong>Documenti archiviati nel cantiere</strong>
      {state.documents.map(file => <div key={file.id} data-testid={`client-doc-file-${file.id}`}>
        <span>{file.display_name}</span>
        <small>{formatDate((file.created_at || "").slice(0, 10))} · {file.uploader_name}</small>
        <button className="icon-btn" title="Apri documento" data-testid={`client-doc-open-${file.id}`} onClick={() => openPracticeFile(file)}><ArrowUpRight size={14} /></button>
        <button className="icon-btn" title="Rimuovi documento" data-testid={`client-doc-delete-${file.id}`} onClick={() => removeDoc(file)}><Trash2 size={14} /></button>
      </div>)}
      {!state.documents.length && <p className="practice-hint" data-testid="client-docs-archive-empty">Nessun documento generato: ogni PDF o Word prodotto viene archiviato automaticamente qui.</p>}
    </div>
  </section>;
};
